import {
    CdnObjectDto,
    ImageType,
    InvitationCodeDto,
    TeamAccess,
    TeamDetailsSupportDto,
    TeamLeaderboardVisibility,
    TeamMemberVisibility,
    TeamUpdateSupportRequest
} from '@api/ChallengeClient';
import { FormSection } from '@components/form-section';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { useError } from '@components/hooks/useError';
import { ImageUpload } from '@components/image-upload';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { getCdnImage } from '@components/utils/cdn-image';
import { Api } from '@core/api';
import { I18n } from '@core/i18n';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { formatEnumLabel } from '@core/utils/format-enum-label';
import { Pencil, RefreshCw, Save, Trash2 } from 'lucide-react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DeleteTeamDialog } from '../../delete-team-dialog';
import { TeamContext } from '..';

interface TeamFormState {
    access: TeamAccess;
    description: string;
    image?: CdnObjectDto;
    leaderboardVisibility: TeamLeaderboardVisibility;
    memberLimit: string;
    memberVisibility: TeamMemberVisibility;
    name: string;
}

interface ApiRequestError {
    status?: number;
}

const toOptions = <T extends string>(values: T[]) =>
    values.map((value) => ({ label: formatEnumLabel(value), value }));

const ACCESS_OPTIONS = toOptions(Object.values(TeamAccess) as TeamAccess[]);
const MEMBER_VISIBILITY_OPTIONS = toOptions(
    Object.values(TeamMemberVisibility) as TeamMemberVisibility[]
);
const LEADERBOARD_VISIBILITY_OPTIONS = toOptions(
    Object.values(TeamLeaderboardVisibility) as TeamLeaderboardVisibility[]
);

const badgeVariant = (
    value: TeamAccess | TeamMemberVisibility | TeamLeaderboardVisibility
): 'outline' | 'success' | 'warning' =>
    value === TeamAccess.OPEN || value === TeamMemberVisibility.FULL
        ? 'success'
        : value === TeamAccess.REQUIRE_APPROVAL
          ? 'warning'
          : 'outline';

const buildFormState = (team: TeamDetailsSupportDto): TeamFormState => ({
    access: team.access,
    description: team.description || '',
    image: team.image,
    leaderboardVisibility: team.leaderboardVisibility,
    memberLimit: team.memberLimit !== undefined ? `${team.memberLimit}` : '',
    memberVisibility: team.memberVisibility,
    name: team.name
});

interface DetailFieldProps {
    children: React.ReactNode;
    label: string;
}

const DetailField = ({ children, label }: DetailFieldProps) => (
    <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-muted-foreground">
            {label}
        </span>
        {children}
    </div>
);

const TeamDetails = () => {
    const navigate = useNavigate();
    const { challengeClient } = injectTSDI(Api);
    const { formatDateTime } = injectTSDI(I18n);
    const showError = useError();
    const { refreshTeam, team } = React.useContext(TeamContext);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [imageUploadError, setImageUploadError] = React.useState<
        string | undefined
    >(undefined);
    const [imageUploadLoading, setImageUploadLoading] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [requiresReload, setRequiresReload] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [form, setForm] = React.useState<TeamFormState>(() =>
        buildFormState(team)
    );

    const updateTeam = useWrapRequest<
        TeamDetailsSupportDto,
        [number, TeamUpdateSupportRequest]
    >(
        async (teamId, payload) => {
            const response =
                await challengeClient.support.teamSupportControllerUpdate(
                    teamId,
                    payload
                );
            return response.data;
        },
        {
            successMessage: () => 'Team updated'
        }
    );

    const generateInvitationCode = useWrapRequest<InvitationCodeDto, [number]>(
        async (teamId) => {
            const response =
                await challengeClient.support.teamSupportControllerGenerateInvitationCode(
                    teamId
                );
            return response.data;
        },
        {
            successMessage: () => 'Invitation code generated'
        }
    );

    React.useEffect(() => {
        if (!isEditing) {
            setForm(buildFormState(team));
            setImageUploadError(undefined);
            setRequiresReload(false);
            setSaveError(null);
        }
    }, [isEditing, team]);

    const imagePreviewUrl = React.useMemo(
        () =>
            getCdnImage(form.image?.bucketName, form.image?.objectKey, {
                resize: {
                    width: 720,
                    height: 480,
                    fit: 'cover'
                }
            }),
        [form.image]
    );

    const handleEditStart = () => {
        setForm(buildFormState(team));
        setIsEditing(true);
        setRequiresReload(false);
        setSaveError(null);
    };

    const handleCancel = () => {
        setForm(buildFormState(team));
        setIsEditing(false);
        setRequiresReload(false);
        setSaveError(null);
        setImageUploadError(undefined);
    };

    const setField = <K extends keyof TeamFormState>(
        field: K,
        value: TeamFormState[K]
    ) =>
        setForm((current) => ({
            ...current,
            [field]: value
        }));

    const handleImageUpload = async (file: File) => {
        setImageUploadLoading(true);
        setImageUploadError(undefined);

        try {
            const response =
                await challengeClient.support.imageSupportControllerUpload(
                    { type: ImageType.TEAM },
                    { file }
                );

            setField('image', response.data);
        } catch (error) {
            showError(error);
            setImageUploadError('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
        }
    };

    const handleSave = async () => {
        const normalizedName = form.name.trim();
        const normalizedDescription = form.description.trim();
        const normalizedMemberLimit = form.memberLimit.trim();

        if (!normalizedName) {
            setSaveError('Name is required.');
            setRequiresReload(false);
            return;
        }

        if (normalizedMemberLimit !== '') {
            const parsedMemberLimit = Number(normalizedMemberLimit);

            if (!Number.isInteger(parsedMemberLimit) || parsedMemberLimit < 0) {
                setSaveError(
                    'Member limit must be a non-negative whole number.'
                );
                setRequiresReload(false);
                return;
            }
        }

        setSaveError(null);
        setRequiresReload(false);

        const payload: TeamUpdateSupportRequest = {
            access: form.access,
            description: normalizedDescription || undefined,
            image: form.image,
            leaderboardVisibility: form.leaderboardVisibility,
            memberLimit:
                normalizedMemberLimit === ''
                    ? undefined
                    : Number(normalizedMemberLimit),
            memberVisibility: form.memberVisibility,
            name: normalizedName
        };

        try {
            await updateTeam.request([team.id, payload], { throwError: true });
            await refreshTeam();
            setIsEditing(false);
        } catch (error) {
            const requestError = error as ApiRequestError;

            if (requestError.status === 409) {
                setRequiresReload(true);
                setSaveError(
                    'This team was updated elsewhere. Reload the latest data before saving again.'
                );
                return;
            }

            setSaveError('Unable to save team changes.');
        }
    };

    const handleReload = async () => {
        try {
            await refreshTeam();
            setIsEditing(false);
            setRequiresReload(false);
            setSaveError(null);
        } catch {
            return;
        }
    };

    const handleGenerateInvitationCode = async () => {
        try {
            await generateInvitationCode.request([team.id], {
                throwError: true
            });
            await refreshTeam();
        } catch {
            return;
        }
    };

    return (
        <>
            <div className="mb-6 flex flex-wrap items-center justify-end gap-2">
                {isEditing ? (
                    <>
                        <Button
                            variant="ghost"
                            disabled={
                                updateTeam.loading ||
                                generateInvitationCode.loading
                            }
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={updateTeam.loading}
                            disabled={generateInvitationCode.loading}
                            onClick={() => {
                                void handleSave();
                            }}
                        >
                            <Save />
                            Save
                        </Button>
                    </>
                ) : (
                    <Button
                        disabled={generateInvitationCode.loading}
                        onClick={handleEditStart}
                    >
                        <Pencil />
                        Edit
                    </Button>
                )}
                <Button
                    loading={generateInvitationCode.loading}
                    disabled={updateTeam.loading}
                    onClick={() => {
                        void handleGenerateInvitationCode();
                    }}
                >
                    <RefreshCw />
                    Generate Invitation Code
                </Button>
                <Button
                    variant="outline"
                    disabled={
                        updateTeam.loading || generateInvitationCode.loading
                    }
                    onClick={() => setDeleteDialogOpen(true)}
                >
                    <Trash2 />
                    Delete
                </Button>
            </div>

            {saveError && (
                <Alert
                    variant={requiresReload ? 'warning' : 'destructive'}
                    className="mb-6"
                >
                    <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                        <span>{saveError}</span>
                        {requiresReload && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleReload}
                            >
                                Reload
                            </Button>
                        )}
                    </AlertDescription>
                </Alert>
            )}

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-1">
                    <FormSection title="Team Image">
                        {isEditing ? (
                            <ImageUpload
                                onUpload={handleImageUpload}
                                onRemove={() => setField('image', undefined)}
                                loading={imageUploadLoading}
                                error={imageUploadError}
                                previewUrl={imagePreviewUrl}
                                label="Team image"
                                helperText="Drag and drop a team image here, or click to select"
                            />
                        ) : imagePreviewUrl ? (
                            <img
                                src={imagePreviewUrl}
                                alt={`${team.name} team`}
                                className="w-full rounded-lg object-cover"
                                style={{ aspectRatio: '4 / 3' }}
                            />
                        ) : (
                            <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed">
                                <span className="text-sm text-muted-foreground">
                                    No team image
                                </span>
                            </div>
                        )}
                    </FormSection>
                </div>

                <div className="flex flex-col gap-6 lg:col-span-2">
                    <FormSection title="Team Settings">
                        <div className="grid gap-4 md:grid-cols-2">
                            {isEditing ? (
                                <TextField
                                    label="Name"
                                    type="text"
                                    value={form.name}
                                    onChange={(value) =>
                                        setField('name', String(value))
                                    }
                                    required
                                />
                            ) : (
                                <DetailField label="Name">
                                    <span className="text-sm">{team.name}</span>
                                </DetailField>
                            )}
                            <DetailField label="Community">
                                <Link
                                    to={links.communityDetails(
                                        team.community.id.toString()
                                    )}
                                    className="text-sm font-semibold hover:underline"
                                >
                                    {team.community.name}
                                </Link>
                                <span className="text-xs text-muted-foreground">
                                    {team.community.tenantId || 'No tenant'}
                                </span>
                            </DetailField>
                            <div className="md:col-span-2">
                                {isEditing ? (
                                    <TextField
                                        label="Description"
                                        type="text"
                                        multiline
                                        maxRows={4}
                                        value={form.description}
                                        onChange={(value) =>
                                            setField(
                                                'description',
                                                String(value)
                                            )
                                        }
                                    />
                                ) : (
                                    <DetailField label="Description">
                                        <span className="text-sm">
                                            {team.description ||
                                                'No description'}
                                        </span>
                                    </DetailField>
                                )}
                            </div>
                            <DetailField label="Invitation Code">
                                <span
                                    className={
                                        team.invitationCode
                                            ? 'font-mono text-sm'
                                            : 'text-sm'
                                    }
                                >
                                    {team.invitationCode || 'Not generated'}
                                </span>
                            </DetailField>
                            <DetailField label="Member Count">
                                <span className="text-sm">
                                    {team.memberCount}
                                </span>
                            </DetailField>
                        </div>
                    </FormSection>

                    <FormSection title="Visibility & Access">
                        <div className="grid gap-4 md:grid-cols-2">
                            {isEditing ? (
                                <Select
                                    label="Access"
                                    value={form.access}
                                    options={ACCESS_OPTIONS}
                                    onChange={(value) =>
                                        setField('access', value)
                                    }
                                />
                            ) : (
                                <DetailField label="Access">
                                    <Badge variant={badgeVariant(team.access)}>
                                        {formatEnumLabel(team.access)}
                                    </Badge>
                                </DetailField>
                            )}

                            {isEditing ? (
                                <Select
                                    label="Member Visibility"
                                    value={form.memberVisibility}
                                    options={MEMBER_VISIBILITY_OPTIONS}
                                    onChange={(value) =>
                                        setField('memberVisibility', value)
                                    }
                                />
                            ) : (
                                <DetailField label="Member Visibility">
                                    <Badge
                                        variant={badgeVariant(
                                            team.memberVisibility
                                        )}
                                    >
                                        {formatEnumLabel(team.memberVisibility)}
                                    </Badge>
                                </DetailField>
                            )}

                            {isEditing ? (
                                <Select
                                    label="Leaderboard Visibility"
                                    value={form.leaderboardVisibility}
                                    options={LEADERBOARD_VISIBILITY_OPTIONS}
                                    onChange={(value) =>
                                        setField('leaderboardVisibility', value)
                                    }
                                />
                            ) : (
                                <DetailField label="Leaderboard Visibility">
                                    <Badge
                                        variant={badgeVariant(
                                            team.leaderboardVisibility
                                        )}
                                    >
                                        {formatEnumLabel(
                                            team.leaderboardVisibility
                                        )}
                                    </Badge>
                                </DetailField>
                            )}

                            {isEditing ? (
                                <div className="flex flex-col gap-1">
                                    <TextField
                                        label="Member Limit"
                                        type="number"
                                        value={form.memberLimit}
                                        onChange={(value) =>
                                            setField(
                                                'memberLimit',
                                                String(value)
                                            )
                                        }
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        Leave blank for no limit
                                    </span>
                                </div>
                            ) : (
                                <DetailField label="Member Limit">
                                    <span className="text-sm">
                                        {team.memberLimit ?? 'Unlimited'}
                                    </span>
                                </DetailField>
                            )}
                        </div>
                    </FormSection>

                    <FormSection title="Audit">
                        <div className="grid gap-4 md:grid-cols-3">
                            <DetailField label="Team ID">
                                <span className="text-sm">{team.id}</span>
                            </DetailField>
                            <DetailField label="Created">
                                <span className="text-sm">
                                    {formatDateTime(team.createdDate)}
                                </span>
                            </DetailField>
                            <DetailField label="Last Modified">
                                <span className="text-sm">
                                    {formatDateTime(team.modifiedDate)}
                                </span>
                            </DetailField>
                        </div>
                    </FormSection>
                </div>
            </div>

            <DeleteTeamDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                team={{ id: team.id, name: team.name }}
                onSuccess={() => {
                    setDeleteDialogOpen(false);
                    navigate(
                        links.communityTeams(team.community.id.toString())
                    );
                }}
            />
        </>
    );
};

export default TeamDetails;
